/* eslint-disable */
import { WarningOutlined } from '@ant-design/icons';
import {
  Col,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tooltip, Typography
} from 'antd';
import { TableProps } from 'antd/lib';
import { useAppSelector } from 'app/store';
import useScreenType from 'common/hooks/useScreenType';
import { getAllStaffJoinedProjectService } from 'common/services/extends/payrollPeriods';
import { dateKeyRegex } from 'common/utils/schemas';
import roles, { getPermission } from 'configs/roles';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

interface ProjectStaffJoinedModalProps {
  monthPayroll: string;
  isOpen: boolean;
  handleClose: () => void;
  staffId: number;
}

interface DataType {
  id: number,
  nameProgram?: string,
  staffName?: string,
  timeKeepingName?: string,
  payrollManage?: string,
  cccd: string,
  [key: string]: any;
  totalShiftCount: number;
  totalWorkingDays: number,
  systemWorkingDateWarning: number;
  workingDateWarning: boolean;
  payrollPeriodId?: number;
  payrollId?: number;
  projectId?: number;
}

const ProjectStaffJoinedModal: React.FC<ProjectStaffJoinedModalProps> = ({
  handleClose,
  isOpen,
  staffId,
  monthPayroll
}) => {
  const { t } = useTranslation();
  const rolesUser = useAppSelector((state) => state.auth.roles);
  const { isDesktop } = useScreenType();

  const { data: projectStaffJoinedData, isLoading } = useQuery(
    ['projectStaffJoined', staffId],
    () => getAllStaffJoinedProjectService(monthPayroll, staffId),
    {
      enabled: isOpen && !!staffId && !!monthPayroll && getPermission(rolesUser, roles.PAYROLL_PERIOD_STAFF_JOIN_IN_PAYROLL),
    }
  )

  const dataJoined = useMemo(() => {
    if (projectStaffJoinedData && projectStaffJoinedData.length > 0) {
      return projectStaffJoinedData.map((i, index) => {
        const dates = i.attendanceDayData?.reduce((acc: any, curr) => {
          acc[curr.date] = curr.shiftCount;
          return acc;
        }, {});
        return {
          key: index + 1,
          id: i.staffData?.id,
          cccd: i.staffData?.idCardNumber,
          staffName: i.staffData?.fullname,
          timeKeepingName: i.payrollPeriodData?.name,
          nameProgram: i.projectData?.name,
          payrollId: i.payrollData?.id,
          payrollPeriodId: i.payrollPeriodData?.id,
          projectId: i.projectData?.id,
          payrollManage: i.payrollData?.fullname,
          workingDateWarning: i.staffData?.workingDateWarning,
          totalShiftCount: i.staffData?.totalShiftCount || 0,
          totalWorkingDays: i.staffData?.totalWorkingDays || 0,
          systemWorkingDateWarning: i.staffData?.systemWorkingDateWarning || 0,
          ...dates
        } as DataType;
      })
    }
    return [];
  }, [projectStaffJoinedData]);

  const mappingAllDatesFromItemAttendanceDayData = useMemo(() => {
    if (projectStaffJoinedData && projectStaffJoinedData.length > 0) {
      return projectStaffJoinedData.reduce((acc: Array<{ date: string }>, item) => {
        if (item.attendanceDayData && item.attendanceDayData.length > 0) {
          item.attendanceDayData.forEach((attendanceItem) => {
            if (!acc.find(dateItem => dateItem.date === attendanceItem.date)) {
              acc.push({ date: attendanceItem.date });
            }
          });
        }
        return acc;
      }, []).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }
    return [];
  }, [projectStaffJoinedData]);

  const dateColumn: TableProps<DataType>['columns'] = useMemo(() => {
    if (mappingAllDatesFromItemAttendanceDayData && mappingAllDatesFromItemAttendanceDayData.length > 0) {
      return mappingAllDatesFromItemAttendanceDayData?.map((i) => ({
        title: (<div dangerouslySetInnerHTML={{
          __html: `
              <p>${dayjs(i.date).format('DD/MM')}</p>
              <p style="font-size: 12px; font-style: italic; font-weight: 400;">${dayjs(i.date).format('dddd')}</p>
        `}} />),
        dataIndex: i.date,
        key: i.date,
        align: 'center',
        render: (_value: any, _record) => {
          return _value;
        }
      })) || [];
    }
    return [];
  }, [mappingAllDatesFromItemAttendanceDayData]);

  const columns: TableProps<DataType>['columns'] = [
    {
      title: 'STT',
      dataIndex: 'key',
      width: 50,
      align: 'center',
      ...isDesktop ? { fixed: 'left' } : {},
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      ...isDesktop ? { fixed: 'left' } : {},
      align: 'center',
      render: (_value, _record) => {
        return (
          <Space>
            {
              _record.totalWorkingDays > _record.systemWorkingDateWarning ? (
                <Typography.Text style={{ color: '#ff3b30' }}>
                  {t('timesheet.exceedingTheDayTimekeepingThreshold')}
                </Typography.Text>
              ) : (
                <Typography.Text style={{ color: '#52c41a' }}>
                  {t('system.notExceeded')}
                </Typography.Text>
              )
            }
          </Space>
        );
      }
    },
    {
      title: t('timesheet.nameProgram'),
      dataIndex: 'nameProgram',
      width: 100,
      key: 'nameProgram',
      ...isDesktop ? { fixed: 'left' } : {},
    },
    {
      title: t('timesheet.payrollManage'),
      dataIndex: 'payrollManage',
      width: 100,
      key: 'payrollManage',
      ...isDesktop ? { fixed: 'left' } : {},
    },
    {
      title: t('timesheet.timeKeepingName'),
      dataIndex: 'timeKeepingName',
      width: 100,
      key: 'timeKeepingName',
      ...isDesktop ? { fixed: 'left' } : {},
    },
    {
      title: t('system.total'),
      dataIndex: 'totalShiftCount',
      key: 'totalShiftCount',
      width: 50,
      ...isDesktop ? { fixed: 'left' } : {},
      align: 'center',
      render: (_value: any, _record) => {
        return _value;
      }
    },
    {
      title: 'Ngày công',
      dataIndex: 'totalWorkingDays',
      key: 'totalWorkingDays',
      ...isDesktop ? { fixed: 'left' } : {},
      align: 'center',
      render: (_value: any, _record) => {
        return _value;
      }
    },
    ...dateColumn,
  ];

  return (
    <Modal
      title={(
        <Typography.Title
          level={4}
        >
          Chương trình tham gia
        </Typography.Title>
      )}
      open={isOpen}
      footer={null}
      closable={true}
      centered
      width={1600}
      onCancel={() => handleClose()}
    >
      <Spin spinning={isLoading}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: '100%' }}>
            <Row gutter={16} className="u-mb-16 u-mt-8">
              <Col sm={12} xl={12} className="u-mb-8">
                <Typography.Text strong>
                  {t('timesheet.fullnameStaff')}
                  {': '}
                </Typography.Text>
                <Typography.Text>
                  {projectStaffJoinedData?.[0]?.staffData?.fullname || ''}
                </Typography.Text>
              </Col>
              <Col sm={12} xl={12} className="u-mb-8">
                <Typography.Text strong>
                  {t('system.cccd')}
                  {': '}
                </Typography.Text>
                <Typography.Text>
                  {projectStaffJoinedData?.[0]?.staffData?.idCardNumber || ''}
                </Typography.Text>
              </Col>
            </Row>
            <Table<DataType>
              columns={columns}
              dataSource={dataJoined}
              scroll={{ x: 'max-content' }}
              rowClassName={(row) => {
                const classWarning = row.totalWorkingDays > row.systemWorkingDateWarning ? 'salary_warning' : '';
                return `${classWarning}`;
              }}
              bordered
              pagination={false}
              summary={(record) => {
                const totalShiftCount = record.reduce((prev, cur) => {
                  return prev + cur.totalShiftCount
                }, 0);

                const totalWorkingDays = record.reduce((prev, cur) => {
                  return prev + cur.totalWorkingDays
                }, 0);

                const merged = record.reduce((acc: Record<string, number>, item) => {
                  Object.entries(item).forEach(([key, value]) => {
                    if (dateKeyRegex.test(key)) {
                      if (!acc[key]) {
                        acc[key] = value;
                      } else {
                        acc[key] += value ? Number(value) : 0;
                      }
                    }
                  });
                  return acc;
                }, {});

                // Sort the merged object by date keys
                const sortedMerged = Object.entries(merged).sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());

                let classWarning = '';

                if (record.length > 0) {
                  classWarning = totalWorkingDays > record[0].systemWorkingDateWarning ? 'salary_warning' : '';
                }

                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row className={classWarning}>
                      <Table.Summary.Cell index={0} colSpan={5}>
                        <Space>
                          <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                            Tổng:
                          </Typography.Text>
                          {
                            classWarning && (
                              <Tooltip
                                title={<div dangerouslySetInnerHTML={{
                                  __html: `
                              <div>${t('salary.didYou')} ${t('timesheet.exceedingTheDayTimekeepingThreshold')}</div>
                              `}} />}
                              >
                                <WarningOutlined size={30} style={{ color: "#ff3b30" }} />
                              </Tooltip>
                            )
                          }
                        </Space>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="center">
                        <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                          {totalShiftCount}
                        </Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="center">
                        <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                          {totalWorkingDays}
                        </Typography.Text>
                      </Table.Summary.Cell>
                      {
                        sortedMerged?.map(([_, value], index) => (
                          <Table.Summary.Cell index={7 + index} align="center">
                            <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                              {value}
                            </Typography.Text>
                          </Table.Summary.Cell>
                        ))
                      }
                    </Table.Summary.Row>
                  </Table.Summary>
                )
              }}
            />
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default ProjectStaffJoinedModal;
